import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, Search, TrendingUp } from "lucide-react";
import { Reports, Assessments } from "@/lib/api";
import { useSetting } from "@/lib/appSettings";
import { Input } from "@/components/ui/input";

export default function ReportsPage() {
  const headerTitle = useSetting("reports.header.title", "Relatórios funcionais");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const all = await Reports.summary();
        setRows(all);
      } catch {
        toast.error("Não foi possível carregar os relatórios");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.patient_name.toLowerCase().includes(needle));
  }, [rows, q]);

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Secção</div>
        <h1 className="font-display text-4xl text-slate-50 mt-2">{headerTitle}</h1>
        <p className="text-slate-400 text-sm mt-1">Arquivo central de todas as avaliações analisadas — descarrega o PDF ou abre o espaço de avaliação.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="reports-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Pesquisar por paciente…"
          className="pl-9 bg-slate-900/60 border-slate-800 text-slate-100 placeholder:text-slate-500"
        />
      </div>

      {loading ? (
        <div className="text-slate-500">A carregar relatórios…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center text-slate-500 bg-slate-900/30">
          Sem relatórios analisados ainda.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div
              key={r.assessment_id}
              data-testid={`report-row-${r.assessment_id}`}
              className="flex flex-col gap-3 sm:grid sm:grid-cols-[1fr_auto_auto] md:grid-cols-[1.2fr_1fr_auto_auto] sm:gap-4 sm:items-center rounded-xl border border-slate-800 bg-slate-900/40 p-4 sm:p-5 hover:border-sky-500/30 transition glow-card fade-in-up"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 shrink-0 rounded-full bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <Link to={`/patients/${r.patient_id}`} className="text-slate-50 font-medium hover:text-sky-300 block truncate">{r.patient_name}</Link>
                  <div className="text-xs text-slate-500 truncate">{new Date(r.created_at).toLocaleString("pt-PT")}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:hidden md:flex md:items-center md:gap-2 text-xs sm:text-sm">
                <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={1.5} />
                <span className="text-slate-200">{r.score}/100</span>
                <span className="text-slate-500 truncate">· {r.classification}</span>
              </div>
              <div className="flex items-center gap-2 sm:contents">
                <Link
                  to={`/assessments/${r.assessment_id}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid={`open-assessment-${r.assessment_id}`}
                >
                  Abrir
                </Link>
                <a
                  href={Assessments.pdfUrl(r.assessment_id)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold glow-sky"
                  data-testid={`pdf-${r.assessment_id}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
